import { z } from 'zod';
import { baseComponentSchema } from '../component-base.schemas';

// Video component schemas
export const videoThumbnailSchema = z.object({
  url: z.string().url('Invalid thumbnail URL').optional(),
  alt: z.string().max(200, 'Thumbnail alt text is too long').optional(),

  // Upload metadata for custom thumbnails
  mediaId: z.string().uuid('Invalid thumbnail media ID').optional(),
  s3Key: z.string().optional(),
  cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),

  // Auto-generation settings
  useAutoGenerated: z.boolean().default(true),
  timeOffset: z
    .number()
    .min(0, 'Time offset must be non-negative')
    .max(7200, 'Time offset too large (max 2 hours)')
    .optional(),
});

export const videoButtonSchema = z.object({
  id: z.string().uuid('Invalid video button ID').optional(),
  text: z.string().min(1, 'Button text is required').max(50, 'Button text is too long'),
  url: z
    .string()
    .url('Invalid URL')
    .or(z.string().regex(/^\//, 'URL must be absolute or start with /')),
  style: z.enum(['primary', 'secondary', 'outline', 'ghost']).default('primary'),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
  target: z.enum(['_blank', '_self']).optional(),
  order: z.number().int().min(0).optional(),
});

export const videoPlaybackSchema = z.object({
  // Basic playback controls
  autoplay: z.boolean().default(false),
  muted: z.boolean().default(true),
  loop: z.boolean().default(false),
  controls: z.boolean().default(true),

  // Quality and performance
  preload: z.enum(['none', 'metadata', 'auto']).default('metadata'),
  quality: z.enum(['auto', '720p', '1080p', '4k']).optional(),

  // Player behavior
  playOnHover: z.boolean().default(false),
  pauseOnLeave: z.boolean().default(false),
  showPlayButton: z.boolean().default(true),

  // Volume controls
  defaultVolume: z.number().min(0).max(1).default(0.8),
  allowVolumeControl: z.boolean().default(true),

  // Fullscreen and sharing
  allowFullscreen: z.boolean().default(true),
  allowDownload: z.boolean().default(false),
  allowSharing: z.boolean().default(true),

  // Accessibility
  showCaptions: z.boolean().default(false),
  captionsLanguage: z.string().max(10, 'Language code is too long').optional(),
});

export const videoCaptionSchema = z.object({
  url: z.string().url('Invalid caption URL'),
  language: z.string().min(2, 'Language code too short').max(10, 'Language code too long'),
  label: z.string().min(1, 'Caption label is required').max(50, 'Caption label too long'),
  default: z.boolean().default(false).optional(),
});

export const videoSourceSchema = z.object({
  // Source type: upload (self-hosted), youtube, or vimeo
  sourceType: z.enum(['upload', 'youtube', 'vimeo']).default('upload'),

  // Primary video source
  // For upload: the video file URL
  // For youtube/vimeo: the video ID (extracted from URL)
  url: z.string().min(1, 'Video URL or ID is required'),

  // Original embed URL (for display purposes in editor)
  embedUrl: z.string().optional(),

  // MIME type (only applicable for uploads)
  mimeType: z
    .string()
    .regex(/^video\//, 'Invalid video MIME type')
    .optional(),

  // Upload metadata (only applicable for uploads)
  mediaId: z.string().uuid('Invalid video media ID').optional(),
  s3Key: z.string().optional(),
  cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),

  // Video metadata
  duration: z
    .number()
    .min(0, 'Duration must be non-negative')
    .max(21600, 'Video too long (max 6 hours)')
    .optional(),
  width: z.number().int().min(1).max(7680).optional(), // Support up to 8K width
  height: z.number().int().min(1).max(4320).optional(), // Support up to 8K height
  fileSize: z.number().int().min(0).optional(),

  // Alternative sources for different formats/qualities (upload only)
  alternatives: z
    .object({
      webm: z.string().url().optional(),
      ogg: z.string().url().optional(),
      lowQuality: z.string().url().optional(),
      highQuality: z.string().url().optional(),
    })
    .optional(),

  // Captions and subtitles
  captions: z.array(videoCaptionSchema).max(10, 'Too many caption tracks').optional(),
});

export const videoComponentSchema = baseComponentSchema.extend({
  type: z.literal('video'),
  data: z.object({
    // Header content
    title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
    subtitle: z.string().max(300, 'Subtitle is too long').optional(),
    description: z.string().max(1000, 'Description is too long').optional(),

    // Video source and thumbnail
    source: videoSourceSchema,
    thumbnail: videoThumbnailSchema,

    // Playback configuration
    playback: videoPlaybackSchema,

    // Layout and sizing
    aspectRatio: z.enum(['16:9', '4:3', '1:1', '9:16', 'auto']).default('16:9'),
    width: z.enum(['full', 'container', 'fixed']).default('container'),
    maxWidth: z.number().int().min(200).max(2000).optional(),
    alignment: z.enum(['left', 'center', 'right']).default('center'),

    // Call-to-action buttons
    buttons: z.array(videoButtonSchema).max(3, 'Too many buttons'),
    buttonsPosition: z.enum(['below', 'overlay', 'beside']).default('below'),

    // Styling
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid background color format')
      .optional(),
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid text color format')
      .optional(),
    padding: z
      .object({
        top: z.number().int().min(0).max(200).default(40),
        bottom: z.number().int().min(0).max(200).default(40),
        left: z.number().int().min(0).max(200).default(40),
        right: z.number().int().min(0).max(200).default(40),
      })
      .optional(),

    // Video player styling
    playerTheme: z.enum(['default', 'dark', 'light', 'custom']).default('default'),
    borderRadius: z.enum(['none', 'small', 'medium', 'large']).default('medium'),
    border: z
      .object({
        enabled: z.boolean().default(false),
        width: z.number().int().min(0).max(20).optional(),
        color: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid border color')
          .optional(),
        style: z.enum(['solid', 'dashed', 'dotted']).optional(),
      })
      .optional(),
    shadow: z
      .object({
        enabled: z.boolean().default(true),
        size: z.enum(['small', 'medium', 'large']).optional(),
        color: z.string().optional(),
      })
      .optional(),

    // Text styling
    titleColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid title color format')
      .optional(),
    titleSize: z.enum(['small', 'medium', 'large', 'xl']).default('large'),
    subtitleColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid subtitle color format')
      .optional(),
    descriptionColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid description color format')
      .optional(),
    contentAlignment: z.enum(['left', 'center', 'right', 'justify']).default('left'),

    // Interactive features
    showMetadata: z.boolean().default(false),
    showTimestamp: z.boolean().default(true),
    allowComments: z.boolean().default(false),

    // Analytics and tracking
    trackViews: z.boolean().default(true),
    trackEngagement: z.boolean().default(false),

    // Responsive behavior
    responsiveHeight: z.boolean().default(true),
    mobileOptimized: z.boolean().default(true),

    // Animation
    animateOnScroll: z.boolean().default(false),
    animationDelay: z.number().int().min(0).max(2000).default(0),
    loadAnimation: z.enum(['fade', 'slide', 'zoom', 'none']).default('fade'),
  }),
});

// Type exports
export type VideoThumbnail = z.infer<typeof videoThumbnailSchema>;
export type VideoButton = z.infer<typeof videoButtonSchema>;
export type VideoPlayback = z.infer<typeof videoPlaybackSchema>;
export type VideoCaption = z.infer<typeof videoCaptionSchema>;
export type VideoSource = z.infer<typeof videoSourceSchema>;
export type VideoComponent = z.infer<typeof videoComponentSchema>;
